import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, query, orderBy, getDocs, doc, runTransaction, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseconfig';
import { fetchCategories } from '../utils/categories';
import { useAuth } from '../contexts/AuthContext';
import './CreateExpenses.css';
import { USER_ROLES } from '../utils/roles';

const CreateExpenses = () => {
  // Format today's date as YYYY-MM-DD for the date input
  const today = new Date().toISOString().split('T')[0];
  
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [date, setDate] = useState(today);
  const [expenseType, setExpenseType] = useState('regular'); // 'regular', 'stock_in', 'stock_out'
  const [stockItemId, setStockItemId] = useState('');
  const [categories, setCategories] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  if (userRole === USER_ROLES.STAFF) {
    navigate('/staff-savings');
  }

  useEffect(() => {
    const loadData = async () => {
      try {
        const fetchedCategories = await fetchCategories();
        setCategories(fetchedCategories);
        
        // Load stock items
        const itemsRef = collection(db, 'stock_items');
        const q = query(itemsRef, orderBy('name', 'asc'));
        const snapshot = await getDocs(q);
        const items = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setStockItems(items);
      } catch (e) {
        console.error('Error loading data:', e);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!amount || category === '' || !date) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate stock item if expense type is stock_in or stock_out
    if ((expenseType === 'stock_in' || expenseType === 'stock_out') && !stockItemId) {
      setError('Please select a stock item for stock-related expenses');
      return;
    }

    try {
      setError('');
      setSuccess('');
      setLoading(true);

      const expenseAmount = Number(amount);
      const expenseData = {
        title: title || null,
        amount: expenseAmount,
        category,
        date: new Date(date),
        createdAt: Timestamp.now(),
        userId: currentUser.uid,
        userEmail: currentUser.email,
        expenseType: expenseType, // 'regular', 'stock_in', 'stock_out'
        stockItemId: (expenseType === 'stock_in' || expenseType === 'stock_out') ? stockItemId : null
      };

      // Use transaction if stock-related expense
      if (expenseType === 'stock_in' || expenseType === 'stock_out') {
        await runTransaction(db, async (transaction) => {
          const timestamp = Timestamp.now();

          // STEP 1: Read stock item first
          const itemRef = doc(db, 'stock_items', stockItemId);
          const itemDoc = await transaction.get(itemRef);
          
          if (!itemDoc.exists()) {
            throw new Error('Selected stock item not found');
          }

          const item = itemDoc.data();
          const currentBalance = item.currentBalance || 0;

          // STEP 2: Create expense and update stock balance
          const expenseRef = doc(collection(db, 'expenses'));
          transaction.set(expenseRef, expenseData);

          // Update stock item balance
          let newBalance = currentBalance;
          if (expenseType === 'stock_in') {
            newBalance = currentBalance + expenseAmount;
          } else if (expenseType === 'stock_out') {
            newBalance = Math.max(0, currentBalance - expenseAmount);
          }

          transaction.update(itemRef, {
            currentBalance: newBalance,
            updatedAt: timestamp
          });
        });
      } else {
        // Simple expense creation without stock
        await addDoc(collection(db, 'expenses'), expenseData);
      }

      setSuccess('Expense added successfully!');
      
      // Clear form
      setTitle('');
      setAmount('');
      setCategory('');
      setDate(today);
      setExpenseType('regular');
      setStockItemId('');

      // Navigate back to expenses list after 2 seconds
      setTimeout(() => {
        navigate('/expenses');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to add expense');
      console.error('Error adding expense:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="create-expense-container">
      <div className="create-expense-card">
        <h2>Create New Expense</h2>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="category">Item *</label>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            >
              <option value="" disabled>
                Select item
              </option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="amount">Amount *</label>
            <input
              type="number"
              id="amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
              min="0"
              step="0.01"
              required
            />
          </div>

          {/* Title relabeled as Description earlier; now optional */}
          <div className="form-group">
            <label htmlFor="title">Description</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter a short note (optional)"
            />
          </div>

          <div className="form-group">
            <label htmlFor="date">Date *</label>
            <input
              type="date"
              id="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="expenseType">Expense Type *</label>
            <select
              id="expenseType"
              value={expenseType}
              onChange={(e) => {
                setExpenseType(e.target.value);
                if (e.target.value === 'regular') {
                  setStockItemId('');
                }
              }}
              required
            >
              <option value="regular">Regular Expense</option>
              <option value="stock_in">Stock In (Add to Stock)</option>
              <option value="stock_out">Stock Out (Remove from Stock)</option>
            </select>
            <small className="form-help">
              {expenseType === 'stock_in' && 'This expense adds money to a stock item balance'}
              {expenseType === 'stock_out' && 'This expense removes money from a stock item balance'}
              {expenseType === 'regular' && 'Regular expense not related to stock'}
            </small>
          </div>

          {(expenseType === 'stock_in' || expenseType === 'stock_out') && (
            <div className="form-group">
              <label htmlFor="stockItemId">Stock Item *</label>
              <select
                id="stockItemId"
                value={stockItemId}
                onChange={(e) => setStockItemId(e.target.value)}
                required
              >
                <option value="">Select stock item</option>
                {stockItems.filter(item => item.isActive !== false).map(item => {
                  const balance = item.currentBalance || 0;
                  return (
                    <option key={item.id} value={item.id}>
                      {item.name} (Balance: {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(balance)})
                    </option>
                  );
                })}
              </select>
              {stockItemId && (() => {
                const selectedItem = stockItems.find(i => i.id === stockItemId);
                if (selectedItem) {
                  const balance = selectedItem.currentBalance || 0;
                  return (
                    <small className="form-help">
                      Current balance: {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(balance)}
                      {expenseType === 'stock_out' && balance < Number(amount) && (
                        <span style={{ color: '#dc3545', display: 'block', marginTop: '4px' }}>
                          Warning: This will result in a negative balance
                        </span>
                      )}
                    </small>
                  );
                }
                return null;
              })()}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={() => navigate('/expenses')}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateExpenses;
